import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { ReportRow, ExecutionGroup, ReportSummary, ReportStep } from '../models/report.model';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { MessageHelperService } from '../../../base-classes/master-message-service';
import { AuthService } from '../../../auth/login/services/auth.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { APP_CONFIG } from '../../../config/app.config';
import { signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';

export class ReportViewModel extends MasterViewModel<ReportRow> {
    reportDialog: boolean = false;
    selectedReport: ReportRow | null = null;

    // Raw data
    allReports = signal<ReportRow[]>([]);

    // Filters
    selectedClient = signal<string | null>(null);
    selectedPackage = signal<string | null>(null);
    selectedTestType = signal<string[]>([]);
    selectedFunctionalArea = signal<string[]>([]);
    selectedStatus = signal<string | null>(null);
    selectedScenario = signal<string | null>(null);
    dateRange = signal<Date[] | null>(null);
    maxDate = new Date();
isClientDropdownDisabled = signal<boolean>(false);
    showDownloadMenu = signal<boolean>(false);

    // Screenshot dialog
    displayScreenshotDialog = signal<boolean>(false);
    selectedScreenshot = signal<string>('');

    // Drawer state
    drawerVisible = false;
    drawerScenario: ReportRow | null = null;
    drawerSteps = signal<ReportStep[]>([]);
    drawerShowFailuresOnly = signal<boolean>(false);
    drawerFilteredSteps = signal<ReportStep[]>([]);

    // Step detail expansion tracking (step id -> expanded)
    expandedStepLogs: Record<string, boolean> = {};

    // Summary
    summary = signal<ReportSummary>({
        totalExecutions: 0,
        totalScenarios: 0,
        totalSteps: 0,
        totalPassed: 0,
        totalFailed: 0,
        totalStopped: 0,
        passRate: 0,
    });

    // Dropdown options
    clientOptions = signal<{ label: string; value: string }[]>([]);
    packageOptions = signal<{ label: string; value: string }[]>([]);
    testTypeOptions = signal<{ label: string; value: string }[]>([]);
    functionalAreaOptions = signal<{ label: string; value: string }[]>([]);
    scenarioOptions = signal<{ label: string; value: string }[]>([]);
    statusOptions = [
        { label: 'Passed', value: 'Passed' },
        { label: 'Failed', value: 'Failed' },
        { label: 'Stopped', value: 'Stopped' }
    ];

    // Internal: all functional areas for the selected client (before testType filter)
    private allFunctionalAreas = signal<{ id: string; name: string; testTypeId: string | null; displayLabel: string }[]>([]);

    // Filtered + grouped data
    executionGroups = signal<ExecutionGroup[]>([]);

    private reportBaseUrl: string;

    constructor(
        http: HttpClient,
        private messageService: MessageHelperService,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private scenarioService: ScenarioService,
        private functionalAreaService: FuntionalAreaService,
    ) {
        const baseUrl = `${environment.apiUrl}/report`;
        const moduleName = 'Report';
        super(http, baseUrl, moduleName);
        this.reportBaseUrl = baseUrl;
    }

    public init(): void {
        this.loadClients();
        this.loadTestTypes();
        this.loadReports();
    }

    // ===== Data Loading =====

    public loadReports(): void {
        this.getAll().then((list: ReportRow[]) => {
            const reports = list || [];
            this.allReports.set(reports);
            this.buildPackageOptions(reports);
            this.applyFilters();
        }).catch(() => {
            this.messageService.error('Unable to get reports list');
        });
    }

    private loadClients(): void {
        this.scenarioService.getClientsAll().subscribe({
            next: (response: any) => {
                const clients = response?.data || response || [];
                const clientMappedOptions = clients.map((c: any) => ({ label: c.name, value: c.id }));

                const userInfo = this.authService.getUserInfoFromToken();
                const isSystemUser = userInfo?.isSystemUser === true;
                const opts = isSystemUser
                    ? [{ label: 'Master Client', value: 'default' }, ...clientMappedOptions]
                    : clientMappedOptions;

                this.clientOptions.set(opts);

                if (isSystemUser) {
                    this.selectedClient.set('default');
                    this.isClientDropdownDisabled.set(false);
                    this.loadFunctionalAreasForClient('default');
                } else if (clientMappedOptions.length === 1) {
                    const singleClientId = clientMappedOptions[0].value;
                    this.selectedClient.set(singleClientId);
                    this.isClientDropdownDisabled.set(true);
                    this.loadFunctionalAreasForClient(singleClientId);
                } else {
                    this.isClientDropdownDisabled.set(false);
                }
            },
            error: () => {
                const userInfo = this.authService.getUserInfoFromToken();
                const isSystemUser = userInfo?.isSystemUser === true;
                this.clientOptions.set(isSystemUser ? [{ label: 'Master Client', value: 'default' }] : []);
                this.isClientDropdownDisabled.set(false);
            }
        });
    }

    private loadTestTypes(): void {
        this.scenarioService.getReferenceList(['TEST_TYPE']).subscribe({
            next: (dataResult: any) => {
                const opts = (dataResult || []).map((item: any) => ({
                    label: item.referenceValue,
                    value: item.id
                }));
                this.testTypeOptions.set(opts);
            },
            error: () => {
                this.testTypeOptions.set([]);
            }
        });
    }

    private loadFunctionalAreasForClient(clientId: string): void {
        this.functionalAreaService.getAllFunctionalAreas().subscribe({
            next: (res: any) => {
                const areas = (res || [])
                    .filter((item: any) => {
                        if (clientId === 'default') {
                            return item.isActive && item.clientPackageMappingId === null;
                        }
                        return item.clientPackageMapping?.client?.id === clientId;
                    })
                    .map((item: any) => ({
                        id: item.id,
                        name: item.name,
                        testTypeId: item.testType?.id || null,
                        displayLabel: item.testType?.referenceValue
                            ? `${item.name} (${item.testType.referenceValue})`
                            : item.name
                    }));
                this.allFunctionalAreas.set(areas);
                this.applyFunctionalAreaFiltering();
            },
            error: () => {
                this.allFunctionalAreas.set([]);
                this.functionalAreaOptions.set([]);
            }
        });
    }

    private loadScenariosForFunctionalArea(functionalAreaId: string): void {
        this.scenarioService.getScenariosByfunctionalAreaId(functionalAreaId).subscribe({
            next: (scenarios: any[]) => {
                const opts = (scenarios || []).map((s: any) => ({
                    label: s.scenarioName,
                    value: s.id
                }));
                this.scenarioOptions.set(opts);
            },
            error: () => {
                this.scenarioOptions.set([]);
            }
        });
    }

    private buildPackageOptions(reports: ReportRow[]): void {
        const packages = new Map<string, string>();
        for (const r of reports) {
            if (r.package?.id && r.package.packageName) {
                packages.set(r.package.id, r.package.packageName);
            }
        }
        this.packageOptions.set(Array.from(packages, ([value, label]) => ({ label, value })));
    }

    private applyFunctionalAreaFiltering(): void {
        const selectedTestTypeIds = this.selectedTestType();
        const allAreas = this.allFunctionalAreas();

        if (!selectedTestTypeIds || selectedTestTypeIds.length === 0) {
            this.functionalAreaOptions.set([]);
            return;
        }

        const filtered = allAreas.filter(area =>
            area.testTypeId && selectedTestTypeIds.includes(area.testTypeId)
        );
        this.functionalAreaOptions.set(filtered.map(a => ({
            label: a.displayLabel,
            value: a.id
        })));
    }

    // ===== Filtering =====

    public applyFilters(): void {
        this.collapseAllSteps();
        let filtered = this.allReports();

        const clientId = this.selectedClient();
        const packageId = this.selectedPackage();
        const testTypeIds = this.selectedTestType();
        const areaIds = this.selectedFunctionalArea();
        const status = this.selectedStatus();
        const scenarioId = this.selectedScenario();
        const dates = this.dateRange();

        // If no client selected and user has multiple clients, show nothing until they pick one
        if (!clientId && this.clientOptions().length > 1) {
            this.executionGroups.set([]);
            this.computeSummary([]);
            return;
        }

        if (clientId && clientId !== 'default') {
            filtered = filtered.filter(r => r.clientId === clientId);
        } else if (clientId === 'default') {
            filtered = filtered.filter(r => !r.clientId);
        }
        if (packageId) {
            filtered = filtered.filter(r => r.packageId === packageId);
        }
        if (testTypeIds && testTypeIds.length > 0) {
            filtered = filtered.filter(r => {
                const typeId = r.functionalArea?.testType?.id || r.testTypeId;
                return typeId && testTypeIds.includes(typeId);
            });
        }
        if (areaIds && areaIds.length > 0) {
            filtered = filtered.filter(r => r.functionalAreaId && areaIds.includes(r.functionalAreaId));
        }
        if (scenarioId) {
            filtered = filtered.filter(r => r.scenarioId === scenarioId);
        }
        if (status) {
            filtered = filtered.filter(r => {
                const scenarioStatus = this.getRowStatus(r);
                return scenarioStatus === status;
            });
        }
        if (dates && dates.length === 2 && dates[0]) {
            const from = new Date(dates[0]);
            from.setHours(0, 0, 0, 0);
            filtered = filtered.filter(r => {
                if (!r.executedOn) return false;
                return new Date(r.executedOn) >= from;
            });
            if (dates[1]) {
                const to = new Date(dates[1]);
                to.setHours(23, 59, 59, 999);
                filtered = filtered.filter(r => {
                    if (!r.executedOn) return false;
                    return new Date(r.executedOn) <= to;
                });
            }
        }

        const groups = this.groupByExecution(filtered);
        this.executionGroups.set(groups);
        this.computeSummary(filtered);
    }

    private groupByExecution(reports: ReportRow[]): ExecutionGroup[] {
        const map = new Map<string, ReportRow[]>();

        for (const r of reports) {
            const key = r.executionId || r.id;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(r);
        }

        return Array.from(map.entries()).map(([executionId, scenarios]) => {
            const isStopped = (r: ReportRow) => {
                const s = (r.status || '').toString().toLowerCase();
                return s === 'stopped' || s === 'aborted';
            };
            const isFailed = (r: ReportRow) => {
                const s = (r.status || '').toString().toLowerCase();
                return s === 'failed' || s === 'error';
            };
            const isPassed = (r: ReportRow) => {
                const s = (r.status || '').toString().toLowerCase();
                return s === 'passed' || s === 'success';
            };

            const totalSteps = scenarios.reduce((sum, s) => sum + (s.numberOfSteps || 0), 0);
            const totalPassed = scenarios.filter(isPassed).length;
            const totalFailed = scenarios.filter(isFailed).length;
            const totalStopped = scenarios.filter(isStopped).length;

            const hasFailed = scenarios.some(s => isFailed(s));
            const hasStopped = scenarios.some(s => isStopped(s));
            let overallStatus: string;
            if (hasFailed) {
                overallStatus = 'Failed';
            } else if (hasStopped) {
                overallStatus = 'Stopped';
            } else {
                overallStatus = 'Passed';
            }

            const clientName = scenarios[0].client?.name ?? '-';
            const packageName = scenarios[0].package?.packageName ?? '-';
            const testTypeName = scenarios[0].functionalArea?.testType?.referenceValue || scenarios[0].testType?.referenceValue || '-';
            const functionalAreaName = scenarios[0].functionalArea?.name ?? '-';
            const validatedScenarios = totalPassed + totalFailed;
            const passRate = validatedScenarios > 0 ? Math.round((totalPassed / validatedScenarios) * 1000) / 10 : 0;
            const scenarioNames = scenarios.map(s => s.scenario?.scenarioName || '').join(' ');
            const executedOn = scenarios[0].executedOn;
            const user = scenarios[0].executedByUser;
            const executedBy = user ? ((user.firstName || '') + ' ' + (user.lastName || '')).trim() : (scenarios[0].executedBy || null);
            const tenantName = scenarios[0].clientTenant?.tenantName || null;

            const searchText = [
                clientName, packageName, testTypeName, functionalAreaName,
                scenarioNames, String(totalSteps), String(totalPassed), String(totalFailed),
                `${passRate}%`, overallStatus, executedOn ? this.formatDate(executedOn) : '',
                executedBy || '', tenantName || ''
            ].join(' ').toLowerCase();

            return {
                executionId,
                clientName,
                packageName,
                testTypeName,
                functionalAreaName,
                scenarioCount: scenarios.length,
                totalSteps,
                totalPassed,
                totalFailed,
                totalStopped,
                passRate,
                overallStatus,
                executedOn,
                executedBy,
                tenantName,
                scenarios,
                isExpanded: false,
                searchText,
            };
        });
    }

    private computeSummary(reports: ReportRow[]): void {
        const totalExecutions = new Set(reports.map(r => r.executionId).filter(Boolean)).size;
        const totalScenarios = reports.length;
        const totalSteps = reports.reduce((sum, r) => sum + (r.numberOfSteps || 0), 0);

        // Count scenarios by status (not step counts) — stopped is a separate non-passing category
        const isStopped = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'stopped' || s === 'aborted';
        };
        const isFailed = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'failed' || s === 'error';
        };
        const isPassed = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'passed' || s === 'success';
        };

        const totalPassed = reports.filter(isPassed).length;
        const totalFailed = reports.filter(isFailed).length;
        const totalStopped = reports.filter(isStopped).length;

        // Pass rate excludes stopped scenarios (industry standard — Playwright/Allure pattern)
        const validatedScenarios = totalPassed + totalFailed;
        const passRate = validatedScenarios > 0
            ? Math.round((totalPassed / validatedScenarios) * 1000) / 10
            : 0;

        this.summary.set({ totalExecutions, totalScenarios, totalSteps, totalPassed, totalFailed, totalStopped, passRate });
    }

    // ===== Status Helpers =====

    public getRowStatus(r: ReportRow): string {
        if (r.statusRef?.referenceValue) return r.statusRef.referenceValue;
        if (r.executionStatusRef?.referenceValue) return r.executionStatusRef.referenceValue;
        if (r.status) return r.status;
        if (r.executionStatus) return r.executionStatus;
        return (Number(r.failed) || 0) === 0 ? 'Passed' : 'Failed';
    }

    public calcStopped(r: ReportRow): number {
        return Math.max(0, (r.numberOfSteps || 0) - (Number(r.passed) || 0) - (Number(r.failed) || 0));
    }

    public getStatusSeverity(status: string): 'success' | 'danger' | 'warn' | 'info' {
        switch (status) {
            case 'Passed': case 'Success': case 'success': return 'success';
            case 'Failed': case 'failed': return 'danger';
            case 'Stopped': return 'warn';
            default: return 'info';
        }
    }

    // ===== Expand / Steps =====

    public toggleExpand(group: ExecutionGroup): void {
        group.isExpanded = !group.isExpanded;
    }

    // ===== Drawer =====

    public openDrawer(scenario: ReportRow): void {
        this.drawerScenario = scenario;
        this.drawerVisible = true;
        this.drawerShowFailuresOnly.set(false);
        this.expandedStepLogs = {};

        if (scenario.steps && scenario.steps.length > 0) {
            this.drawerSteps.set(scenario.steps);
            this.updateDrawerFilteredSteps();
        } else {
            this.drawerSteps.set([]);
            this.drawerFilteredSteps.set([]);
            this.loadStepsForDrawer(scenario);
        }
    }

    public closeDrawer(): void {
        this.drawerVisible = false;
        this.drawerScenario = null;
        this.drawerSteps.set([]);
        this.drawerFilteredSteps.set([]);
        this.expandedStepLogs = {};
    }

    public toggleDrawerFailuresOnly(): void {
        this.drawerShowFailuresOnly.set(!this.drawerShowFailuresOnly());
        this.updateDrawerFilteredSteps();
    }

    private updateDrawerFilteredSteps(): void {
        const steps = this.drawerSteps();
        if (this.drawerShowFailuresOnly()) {
            this.drawerFilteredSteps.set(steps.filter(s => s.passed === false));
        } else {
            this.drawerFilteredSteps.set(steps);
        }
    }

    private loadStepsForDrawer(scenario: ReportRow): void {
        scenario.stepsLoading = true;
        this.http.get<any>(`${this.reportBaseUrl}/steps/${scenario.id}`).subscribe({
            next: (response: any) => {
                const steps: ReportStep[] = response?.data || response || [];
                scenario.steps = steps;
                scenario.stepsLoading = false;

                if (steps.length > 0) {
                    scenario.numberOfSteps = steps.length;
                    scenario.passed = steps.filter(s => s.passed === true).length;
                    scenario.failed = steps.filter(s => s.passed === false).length;
                    this.recalculateGroupTotals(scenario);
                }

                this.drawerSteps.set(steps);
                this.updateDrawerFilteredSteps();
            },
            error: () => {
                scenario.stepsLoading = false;
                this.messageService.error('Unable to load step details');
            }
        });
    }

    public toggleSteps(scenario: ReportRow): void {
        if (scenario.stepsExpanded) {
            scenario.stepsExpanded = false;
            if (scenario.steps) {
                for (const step of scenario.steps) {
                    delete this.expandedStepLogs[step.id];
                }
            }
            return;
        }
        if (!scenario.steps || scenario.steps.length === 0) {
            this.loadStepsForScenario(scenario);
        } else {
            scenario.stepsExpanded = true;
        }
    }

    private loadStepsForScenario(scenario: ReportRow): void {
        scenario.stepsLoading = true;
        this.http.get<any>(`${this.reportBaseUrl}/steps/${scenario.id}`).subscribe({
            next: (response: any) => {
                const steps: ReportStep[] = response?.data || response || [];
                scenario.steps = steps;
                scenario.stepsExpanded = true;
                scenario.stepsLoading = false;

                // Recalculate counts from actual step data
                if (steps.length > 0) {
                    scenario.numberOfSteps = steps.length;
                    scenario.passed = steps.filter(s => s.passed === true).length;
                    scenario.failed = steps.filter(s => s.passed === false).length;
                    this.recalculateGroupTotals(scenario);
                }
            },
            error: () => {
                scenario.stepsLoading = false;
                this.messageService.error('Unable to load step details');
            }
        });
    }

    private recalculateGroupTotals(scenario: ReportRow): void {
        const groups = this.executionGroups();
        const group = groups.find(g => g.scenarios.includes(scenario));
        if (!group) return;

        const isStopped = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'stopped' || s === 'aborted';
        };
        const isFailed = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'failed' || s === 'error';
        };
        const isPassed = (r: ReportRow) => {
            const s = (r.status || '').toString().toLowerCase();
            return s === 'passed' || s === 'success';
        };

        group.totalSteps = group.scenarios.reduce((sum, s) => sum + (s.numberOfSteps || 0), 0);
        group.totalPassed = group.scenarios.filter(isPassed).length;
        group.totalFailed = group.scenarios.filter(isFailed).length;
        group.totalStopped = group.scenarios.filter(isStopped).length;

        const validated = group.totalPassed + group.totalFailed;
        group.passRate = validated > 0 ? Math.round((group.totalPassed / validated) * 1000) / 10 : 0;

        const hasFailed = group.scenarios.some(s => isFailed(s));
        const hasStopped = group.scenarios.some(s => isStopped(s));
        if (hasFailed) {
            group.overallStatus = 'Failed';
        } else if (hasStopped) {
            group.overallStatus = 'Stopped';
        } else {
            group.overallStatus = 'Passed';
        }

        this.executionGroups.set([...groups]);
        this.computeSummary(groups.flatMap(g => g.scenarios));
    }

    // ===== Filter Change Handlers (cascading) =====

    public onClientChange(clientId: string | null): void {
        // If cleared, system users always fall back to 'default'
        if (!clientId) {
            const userInfo = this.authService.getUserInfoFromToken();
            if (userInfo?.isSystemUser === true) {
                clientId = 'default';
            }
        }

        this.selectedClient.set(clientId);
        this.selectedPackage.set(null);
        this.selectedTestType.set([]);
        this.selectedFunctionalArea.set([]);
        this.selectedScenario.set(null);
        this.allFunctionalAreas.set([]);
        this.functionalAreaOptions.set([]);
        this.scenarioOptions.set([]);

        if (clientId) {
            this.loadFunctionalAreasForClient(clientId);
        }
        this.applyFilters();
    }

    public onPackageChange(packageId: string | null): void {
        this.selectedPackage.set(packageId);
        this.applyFilters();
    }

    public onTestTypeChange(testTypeIds: string[]): void {
        this.selectedTestType.set(testTypeIds);
        this.selectedFunctionalArea.set([]);
        this.selectedScenario.set(null);
        this.scenarioOptions.set([]);
        this.applyFunctionalAreaFiltering();
        this.applyFilters();
    }

    public onFunctionalAreaChange(areaIds: string[]): void {
        this.selectedFunctionalArea.set(areaIds);
        this.selectedScenario.set(null);
        this.scenarioOptions.set([]);
        if (areaIds && areaIds.length === 1) {
            this.loadScenariosForFunctionalArea(areaIds[0]);
        }
        this.applyFilters();
    }

    public onScenarioChange(scenarioId: string | null): void {
        this.selectedScenario.set(scenarioId);
        this.applyFilters();
    }

    public onStatusChange(status: string | null): void {
        this.selectedStatus.set(status);
        this.applyFilters();
    }

    public onDateRangeChange(dates: Date[] | null): void {
        this.dateRange.set(dates);
        if (dates && dates.length === 2 && dates[1]) {
            this.applyFilters();
        }
    }

    public onDateRangeClose(): void {
        this.applyFilters();
    }

    // ===== Filter UI Helpers =====

    public clearFilters(): void {
        const wasDisabled = this.isClientDropdownDisabled();
        const userInfo = this.authService.getUserInfoFromToken();
        const isSystemUser = userInfo?.isSystemUser === true;

        // Keep client selection for disabled (single-client) users and system users (Default)
        const resetClient = wasDisabled ? this.selectedClient() : isSystemUser ? 'default' : null;

        this.selectedClient.set(resetClient);
        this.selectedPackage.set(null);
        this.selectedTestType.set([]);
        this.selectedFunctionalArea.set([]);
        this.selectedStatus.set(null);
        this.selectedScenario.set(null);
        this.dateRange.set(null);
        this.functionalAreaOptions.set([]);
        this.scenarioOptions.set([]);

        if (resetClient) {
            this.loadFunctionalAreasForClient(resetClient);
        } else {
            this.allFunctionalAreas.set([]);
        }
        this.applyFilters();
    }

    public hasActiveFilters(): boolean {
        return !!(
            this.selectedClient() ||
            this.selectedPackage() ||
            this.selectedTestType().length > 0 ||
            this.selectedFunctionalArea().length > 0 ||
            this.selectedStatus() ||
            this.selectedScenario() ||
            this.dateRange()
        );
    }

    public getActiveFilterCount(): number {
        let count = 0;
        if (this.selectedClient()) count++;
        if (this.selectedPackage()) count++;
        if (this.selectedTestType().length > 0) count++;
        if (this.selectedFunctionalArea().length > 0) count++;
        if (this.selectedStatus()) count++;
        if (this.selectedScenario()) count++;
        if (this.dateRange()) count++;
        return count;
    }

    public getActiveFilterChips(): { label: string; field: string }[] {
        const chips: { label: string; field: string }[] = [];
        const clientId = this.selectedClient();
        if (clientId) {
            const opt = this.clientOptions().find(o => o.value === clientId);
            chips.push({ label: `Client: ${opt?.label || clientId}`, field: 'client' });
        }
        const packageId = this.selectedPackage();
        if (packageId) {
            const opt = this.packageOptions().find(o => o.value === packageId);
            chips.push({ label: `Package: ${opt?.label || packageId}`, field: 'package' });
        }
        const testTypeIds = this.selectedTestType();
        if (testTypeIds.length > 0) {
            const labels = testTypeIds.map(id => {
                const opt = this.testTypeOptions().find(o => o.value === id);
                return opt?.label || id;
            });
            chips.push({ label: `Test Type: ${labels.join(', ')}`, field: 'testType' });
        }
        const areaIds = this.selectedFunctionalArea();
        if (areaIds.length > 0) {
            const labels = areaIds.map(id => {
                const opt = this.functionalAreaOptions().find(o => o.value === id);
                return opt?.label || id;
            });
            chips.push({ label: `Area: ${labels.join(', ')}`, field: 'functionalArea' });
        }
        const scenarioId = this.selectedScenario();
        if (scenarioId) {
            const opt = this.scenarioOptions().find(o => o.value === scenarioId);
            chips.push({ label: `Scenario: ${opt?.label || scenarioId}`, field: 'scenario' });
        }
        const status = this.selectedStatus();
        if (status) {
            chips.push({ label: `Status: ${status}`, field: 'status' });
        }
        const dates = this.dateRange();
        if (dates && dates[0]) {
            const from = this.formatDate(dates[0].toISOString());
            const to = dates[1] ? this.formatDate(dates[1].toISOString()) : 'now';
            chips.push({ label: `Date: ${from} - ${to}`, field: 'dateRange' });
        }
        return chips;
    }

    public removeFilterChip(field: string): void {
        switch (field) {
            case 'client': this.onClientChange(null); break;
            case 'package': this.onPackageChange(null); break;
            case 'testType': this.onTestTypeChange([]); break;
            case 'functionalArea': this.onFunctionalAreaChange([]); break;
            case 'scenario': this.onScenarioChange(null); break;
            case 'status': this.onStatusChange(null); break;
            case 'dateRange': this.dateRange.set(null); this.applyFilters(); break;
        }
    }

    // ===== Formatting =====

    public formatDate(dateStr: string | null): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).replace(',', '');
    }

    // ===== Dialog =====

    public hideDialog(): void {
        this.selectedReport = null;
        this.reportDialog = false;
    }

    public openNew(report: ReportRow): void {
        this.selectedReport = report;
        if (!report.steps || report.steps.length === 0) {
            this.loadStepsForScenario(report);
        }
        this.reportDialog = true;
    }

    public onGlobalFilter(table: Table, event: Event): void {
        table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
    }

    // ===== Screenshot & Log Helpers =====

    public viewScreenshot(screenshotUrl: string): void {
        this.selectedScreenshot.set(`${APP_CONFIG.FILE_URL}/${screenshotUrl}`);
        this.displayScreenshotDialog.set(true);
    }

    public getScreenshotFullUrl(screenshotUrl: string | null): string {
        return screenshotUrl ? `${APP_CONFIG.FILE_URL}/${screenshotUrl}` : '';
    }

    public formatDuration(ms: number | null): string {
        if (ms === null || ms === undefined) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    }

    public parseLogText(logText: string | null): any {
        if (!logText) return null;
        try {
            return JSON.parse(logText);
        } catch {
            return null;
        }
    }

    public toggleStepLog(stepId: string): void {
        this.expandedStepLogs[stepId] = !this.expandedStepLogs[stepId];
    }

    public isStepLogExpanded(stepId: string): boolean {
        return !!this.expandedStepLogs[stepId];
    }

    public collapseAllSteps(): void {
        this.expandedStepLogs = {};
        for (const group of this.executionGroups()) {
            group.isExpanded = false;
            for (const scenario of group.scenarios) {
                scenario.stepsExpanded = false;
            }
        }
    }

    // ===== Export =====

    private getExportRows(useFiltered: boolean): { headers: string[]; rows: string[][] } {
        const groups = useFiltered ? this.executionGroups() : this.groupByExecution(this.allReports());
        const headers = ['Client', 'Package', 'Test Type', 'Functional Area', 'Scenario', 'Steps', 'Passed', 'Failed', 'Stopped', 'Pass Rate', 'Status', 'Executed On'];
        const rows: string[][] = [];

        for (const group of groups) {
            if (group.scenarioCount === 1) {
                const s = group.scenarios[0];
                const sStopped = Math.max(0, (s.numberOfSteps || 0) - (s.passed || 0) - (s.failed || 0));
                rows.push([
                    group.clientName,
                    group.packageName,
                    group.testTypeName,
                    s.functionalArea?.name || group.functionalAreaName,
                    s.scenario?.scenarioName || '-',
                    String(s.numberOfSteps || 0),
                    String(s.passed || 0),
                    String(s.failed || 0),
                    String(sStopped),
                    `${group.passRate}%`,
                    this.getRowStatus(s),
                    this.formatDate(s.executedOn),
                ]);
            } else {
                // Group summary row
                rows.push([
                    group.clientName,
                    group.packageName,
                    group.testTypeName,
                    group.functionalAreaName,
                    `${group.scenarioCount} Scenarios`,
                    String(group.totalSteps),
                    String(group.totalPassed),
                    String(group.totalFailed),
                    String(group.totalStopped),
                    `${group.passRate}%`,
                    group.overallStatus,
                    this.formatDate(group.executedOn),
                ]);
                // Individual scenario rows
                for (const s of group.scenarios) {
                    const sStopped = Math.max(0, (s.numberOfSteps || 0) - (s.passed || 0) - (s.failed || 0));
                    const sExecuted = (s.passed || 0) + (s.failed || 0);
                    const sPassRate = sExecuted > 0 ? `${Math.round(((s.passed || 0) / sExecuted) * 1000) / 10}%` : '0%';
                    rows.push([
                        '',
                        '',
                        '',
                        s.functionalArea?.name || '-',
                        s.scenario?.scenarioName || '-',
                        String(s.numberOfSteps || 0),
                        String(s.passed || 0),
                        String(s.failed || 0),
                        String(sStopped),
                        sPassRate,
                        this.getRowStatus(s),
                        this.formatDate(s.executedOn),
                    ]);
                }
            }
        }
        return { headers, rows };
    }

    public async exportXlsx(useFiltered: boolean): Promise<void> {
        const { headers, rows } = this.getExportRows(useFiltered);

        const BRAND_PRIMARY = '1B3A5C';
        const BRAND_ACCENT = '2E86C1';
        const HEADER_FONT = 'FFFFFF';
        const BORDER_COLOR = 'B0C4DE';
        const ALT_ROW_BG = 'EBF5FB';

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'ERPA Test Accelerator';
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet('Execution Report', {
            properties: { defaultColWidth: 18 }
        });

        // Column widths
        worksheet.columns = [
            { width: 18 },  // Client
            { width: 18 },  // Package
            { width: 16 },  // Test Type
            { width: 22 },  // Functional Area
            { width: 28 },  // Scenario
            { width: 10 },  // Steps
            { width: 10 },  // Passed
            { width: 10 },  // Failed
            { width: 10 },  // Stopped
            { width: 12 },  // Pass Rate
            { width: 12 },  // Status
            { width: 20 },  // Executed On
        ];

        let currentRow = 1;

        // Logo
        try {
            const logoResponse = await fetch('assets/images/erpa-logo-one2.png');
            if (logoResponse.ok) {
                const logoBuffer = await logoResponse.arrayBuffer();
                const logoImage = workbook.addImage({ buffer: logoBuffer, extension: 'png' });
                worksheet.mergeCells(currentRow, 1, currentRow + 2, 12);
                worksheet.addImage(logoImage, {
                    tl: { col: 0.2, row: currentRow - 1 + 0.2 },
                    ext: { width: 180, height: 55 }
                });
                const logoCell = worksheet.getCell(currentRow, 1);
                logoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
                worksheet.getRow(currentRow).height = 20;
                worksheet.getRow(currentRow + 1).height = 20;
                worksheet.getRow(currentRow + 2).height = 20;
                currentRow += 3;
            }
        } catch { /* logo not available */ }

        // Title
        worksheet.mergeCells(currentRow, 1, currentRow, 12);
        const titleCell = worksheet.getCell(currentRow, 1);
        titleCell.value = 'ERPA Test Accelerator - Execution Report';
        titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: HEADER_FONT } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_PRIMARY } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getRow(currentRow).height = 40;
        currentRow++;

        // Subtitle
        const s = this.summary();
        worksheet.mergeCells(currentRow, 1, currentRow, 12);
        const subtitleCell = worksheet.getCell(currentRow, 1);
        subtitleCell.value = `Generated on: ${new Date().toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
        subtitleCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: BRAND_ACCENT } };
        subtitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F6FC' } };
        subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getRow(currentRow).height = 22;
        currentRow++;

        // Summary row
        worksheet.mergeCells(currentRow, 1, currentRow, 12);
        const summaryCell = worksheet.getCell(currentRow, 1);
        summaryCell.value = `Executions: ${s.totalExecutions}  |  Scenarios: ${s.totalScenarios}  |  Steps: ${s.totalSteps}  |  Passed: ${s.totalPassed}  |  Failed: ${s.totalFailed}  |  Stopped: ${s.totalStopped}  |  Pass Rate: ${s.passRate}%`;
        summaryCell.font = { name: 'Calibri', size: 10, color: { argb: '374151' } };
        summaryCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D6EAF8' } };
        summaryCell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getRow(currentRow).height = 24;
        currentRow++;

        // Spacer
        currentRow++;

        // Table header
        const headerRow = worksheet.getRow(currentRow);
        headers.forEach((header, index) => {
            const cell = headerRow.getCell(index + 1);
            cell.value = header;
            cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: HEADER_FONT } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_ACCENT } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = {
                top: { style: 'thin', color: { argb: BORDER_COLOR } },
                left: { style: 'thin', color: { argb: BORDER_COLOR } },
                bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
                right: { style: 'thin', color: { argb: BORDER_COLOR } },
            };
        });
        headerRow.height = 28;
        currentRow++;

        // Data rows
        rows.forEach((rowData, rowIndex) => {
            const isAlt = rowIndex % 2 === 1;
            const bgColor = isAlt ? ALT_ROW_BG : 'FFFFFF';
            const dataRow = worksheet.getRow(currentRow);

            rowData.forEach((value, colIndex) => {
                const cell = dataRow.getCell(colIndex + 1);
                cell.value = value;
                cell.font = { name: 'Calibri', size: 10, color: { argb: '334155' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                cell.alignment = { vertical: 'middle', horizontal: colIndex >= 5 && colIndex <= 9 ? 'center' : 'left', wrapText: true };
                cell.border = {
                    top: { style: 'thin', color: { argb: BORDER_COLOR } },
                    left: { style: 'thin', color: { argb: BORDER_COLOR } },
                    bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
                    right: { style: 'thin', color: { argb: BORDER_COLOR } },
                };

                // Status column coloring
                if (colIndex === 10) {
                    if (value === 'Passed' || value === 'Success') {
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: '16A34A' } };
                    } else if (value === 'Failed') {
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'DC2626' } };
                    } else if (value === 'Stopped') {
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'D97706' } };
                    }
                }
            });
            dataRow.height = 22;
            currentRow++;
        });

        // Footer
        currentRow++;
        worksheet.mergeCells(currentRow, 1, currentRow, 12);
        const footerCell = worksheet.getCell(currentRow, 1);
        footerCell.value = 'ERPA Test Accelerator | Confidential';
        footerCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: '999999' } };
        footerCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Generate and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.downloadBlob(blob, `execution-reports-${this.getTimestamp()}.xlsx`);
    }

    public exportPdf(useFiltered: boolean): void {
        const { headers, rows } = this.getExportRows(useFiltered);
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();

        // Title bar background
        doc.setFillColor(27, 58, 92); // BRAND_PRIMARY
        doc.rect(0, 0, pageWidth, 18, 'F');
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.text('ERPA Test Accelerator - Execution Report', pageWidth / 2, 12, { align: 'center' });

        // Subtitle bar
        doc.setFillColor(240, 246, 252);
        doc.rect(0, 18, pageWidth, 8, 'F');
        doc.setFontSize(9);
        doc.setTextColor(46, 134, 193); // BRAND_ACCENT
        doc.text(`Generated on: ${new Date().toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageWidth / 2, 23.5, { align: 'center' });

        // Summary bar
        const s = this.summary();
        doc.setFillColor(214, 234, 248);
        doc.rect(0, 26, pageWidth, 8, 'F');
        doc.setFontSize(9);
        doc.setTextColor(55, 65, 81);
        doc.text(`Executions: ${s.totalExecutions}  |  Scenarios: ${s.totalScenarios}  |  Steps: ${s.totalSteps}  |  Passed: ${s.totalPassed}  |  Failed: ${s.totalFailed}  |  Stopped: ${s.totalStopped}  |  Pass Rate: ${s.passRate}%`, pageWidth / 2, 31.5, { align: 'center' });

        // Table
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 37,
            styles: { fontSize: 7, cellPadding: 2, font: 'helvetica' },
            headStyles: { fillColor: [46, 134, 193], textColor: [255, 255, 255], fontStyle: 'bold', lineWidth: 0.1, lineColor: [176, 196, 222] },
            bodyStyles: { textColor: [51, 65, 85] },
            alternateRowStyles: { fillColor: [235, 245, 251] },
            columnStyles: {
                5: { halign: 'center' },
                6: { halign: 'center' },
                7: { halign: 'center' },
                8: { halign: 'center' },
                9: { halign: 'center' },
            },
            didParseCell: (data: any) => {
                if (data.section === 'body' && data.column.index === 10) {
                    const val = data.cell.raw as string;
                    if (val === 'Passed' || val === 'Success') {
                        data.cell.styles.textColor = [22, 163, 74];
                    } else if (val === 'Failed') {
                        data.cell.styles.textColor = [220, 38, 38];
                    } else if (val === 'Stopped') {
                        data.cell.styles.textColor = [217, 119, 6];
                    }
                }
            },
        });

        // Footer on each page
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.setFontSize(8);
            doc.setTextColor(153, 153, 153);
            doc.text('ERPA Test Accelerator | Confidential', pageWidth / 2, pageHeight - 6, { align: 'center' });
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 6, { align: 'right' });
        }

        doc.save(`execution-reports-${this.getTimestamp()}.pdf`);
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    private getTimestamp(): string {
        return new Date().toISOString().slice(0, 10);
    }
}
